import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Save, Clock, HelpCircle, Copy, Check } from 'lucide-react';

export default function GPSTracking() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [vehicles, setVehicles] = useState([]);

  const load = () => {
    apiGet('/gps/settings').then(setCfg).catch((e) => toast.error(e?.response?.data?.detail || e?.message || 'Failed to load GPS settings'));
    apiGet('/fuel/vehicles').then((d) => setVehicles(d.items || [])).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/gps/settings/idle-threshold', {
        idle_threshold_minutes: Number(cfg.idle_threshold_minutes) || 30,
        active_hours_start: cfg.active_hours_start || '06:00',
        active_hours_end: cfg.active_hours_end || '20:00',
      });
      toast.success('GPS settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate the GPS webhook key? The old key will stop working immediately.')) return;
    try {
      const r = await apiPost('/gps/settings/regenerate-key');
      setNewKey(r.api_key);
      toast.success('New key generated — copy it now');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const link = async (deviceId, vehicleId) => {
    if (!vehicleId) return;
    try {
      await apiPost(`/gps/unknown-devices/${encodeURIComponent(deviceId)}/link?vehicle_id=${vehicleId}`);
      toast.success('Device linked to vehicle');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const copyKey = () => { navigator.clipboard.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (!cfg) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;
  const inp = 'px-3 py-2 border border-slate-300 rounded-sm text-sm';

  return (
    <div data-testid="gps-settings-page">
      <PageHeader overline="Settings · Integrations" title="GPS Tracking" subtitle="Vendor-agnostic vehicle GPS — webhook key, alert thresholds and unlinked devices." />

      {/* Webhook key */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <h3 className="font-display font-bold text-base flex items-center gap-2 mb-1"><KeyRound size={16} className="text-[#1F7A6E]" /> Webhook API Key</h3>
        <p className="text-xs text-slate-500 mb-3">GPS hardware (or your vendor's server) posts telemetry to <code className="bg-slate-100 px-1 rounded">/api/gps/telemetry/ingest</code> with this key in the <code className="bg-slate-100 px-1 rounded">X-GPS-Api-Key</code> header.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-sm bg-slate-100 px-3 py-2 rounded-sm">{cfg.api_key_configured ? '••••••••••••••••' : 'Not configured'}</span>
          <button onClick={regenerate} data-testid="gps-regenerate-key" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 hover:bg-slate-50"><RefreshCw size={13} /> Regenerate</button>
        </div>
        {newKey && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="gps-new-key">
            <div className="text-[11px] font-display font-bold uppercase tracking-widest text-amber-700 mb-1">Copy now — shown only once</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm break-all">{newKey}</code>
              <button onClick={copyKey} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-white border border-amber-300">{copied ? <Check size={13} /> : <Copy size={13} />}</button>
            </div>
          </div>
        )}
      </div>

      {/* Alert thresholds */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <h3 className="font-display font-bold text-base flex items-center gap-2 mb-3"><Clock size={16} className="text-[#1F7A6E]" /> Alert Thresholds</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="text-xs text-slate-500"><span className="block font-display font-bold uppercase tracking-widest text-[10px] mb-1">Idle Alert (minutes)</span>
            <input type="number" min="1" value={cfg.idle_threshold_minutes || 30} onChange={(e) => setCfg({ ...cfg, idle_threshold_minutes: e.target.value })} className={inp + ' w-full'} data-testid="gps-idle-threshold" /></label>
          <label className="text-xs text-slate-500"><span className="block font-display font-bold uppercase tracking-widest text-[10px] mb-1">Active Hours Start</span>
            <input type="time" value={cfg.active_hours_start || '06:00'} onChange={(e) => setCfg({ ...cfg, active_hours_start: e.target.value })} className={inp + ' w-full'} data-testid="gps-hours-start" /></label>
          <label className="text-xs text-slate-500"><span className="block font-display font-bold uppercase tracking-widest text-[10px] mb-1">Active Hours End</span>
            <input type="time" value={cfg.active_hours_end || '20:00'} onChange={(e) => setCfg({ ...cfg, active_hours_end: e.target.value })} className={inp + ' w-full'} data-testid="gps-hours-end" /></label>
        </div>
        <button onClick={save} disabled={saving} data-testid="gps-save-settings" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-[#1F7A6E] text-white hover:bg-[#2f3a5c] disabled:opacity-50"><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
      </div>

      {/* Unknown devices */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display font-bold text-base flex items-center gap-2 mb-1"><HelpCircle size={16} className="text-[#1F7A6E]" /> Unknown Devices</h3>
        <p className="text-xs text-slate-500 mb-3">Devices sending telemetry that aren't linked to any vehicle yet.</p>
        {(cfg.unknown_devices || []).length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="gps-no-unknown">No unknown devices. Everything sending data is linked to a vehicle.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b"><th className="py-2">Device ID</th><th>Last Seen</th><th>Hits</th><th>Link to Vehicle</th></tr></thead>
            <tbody>
              {cfg.unknown_devices.map((d) => (
                <tr key={d.device_id} className="border-b border-slate-100" data-testid={`gps-unknown-${d.device_id}`}>
                  <td className="py-2 font-mono text-xs">{d.device_id}</td>
                  <td className="text-xs text-slate-500">{d.last_seen ? new Date(d.last_seen).toLocaleString('en-IN') : '—'}</td>
                  <td className="text-xs">{d.hit_count || 0}</td>
                  <td>
                    <select onChange={(e) => link(d.device_id, e.target.value)} defaultValue="" className="px-2 py-1 border border-slate-300 rounded-sm text-xs" data-testid={`gps-link-${d.device_id}`}>
                      <option value="" disabled>Select vehicle…</option>
                      {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
